import Service, { service } from '@ember/service'
import { join, parse } from 'teacher-ui-client/utils/uri'
import type RouterService from '@ember/routing/router-service'
import type Store from '@ember-data/store'
import {
  SubscriptionType,
  extractSubscriptionTypeFromRouteInfo,
} from 'teacher-ui-client/subscription-type/subscription-types'
import type Teacher from 'teacher-ui-client/models/teacher'
import { assert } from '@ember/debug'
import type SubscriptionCoordinator from 'teacher-ui-client/models/subscription-coordinator'

/**
 * A service for easy access to the current subscription type, based on the current route, and related properties.
 *
 * DO NOT use this service in routes. In particular in the model hooks. If transitioning between subscription types (eg
 * clicking a subtype tab at the top of the page), during transition, the subtype will be the prior subtype.
 *
 * Note that this affects all routes / pages, since the user can visit any page then click the browser Back button.
 *
 * An eslint rule has been added to disallow any usage of the SubscriptionType service in routes, one place where the
 * subscriptionType from this service may be undefined or a misleading value. In those cases, use
 * modelFor('subscription-type')
 */
export class SubscriptionTypeService extends Service {
  @service router!: RouterService

  @service store!: Store

  /**
   * Use this when a component is potentially rendered outside of the subscription-type area, and needs to deal with the
   * edge case
   *
   * Will return undefined if current route does not have the subscription-type route as an ancestor
   * e.g. login-cards, application_loading
   */
  get currentValue(): SubscriptionType | undefined {
    // When refreshing a page, during the loading substate, if a model hook accesses this property, currentRoute can be
    // null. In that case, use the URL to recognize the current route and infer subscription type from that.
    const currentURLWithoutQueryParams = parse(this.router.currentURL || '/').pathname
    const currentURLWithRoot = join(this.router.rootURL, currentURLWithoutQueryParams).pathname
    const subscriptionType =
      extractSubscriptionTypeFromRouteInfo(this.router.currentRoute) ??
      extractSubscriptionTypeFromRouteInfo(this.router.recognize(currentURLWithRoot))

    return subscriptionType
  }

  /**
   * Returns current subscription type. Only use this when inside the subscription-type area and you don't want to deal
   * with the edge case.
   */
  get current(): SubscriptionType {
    assert('has defined subscription type', this.currentValue)
    return this.currentValue
  }

  get isCurrentReading() {
    return this.current === SubscriptionType.Reading
  }

  get isCurrentMaths() {
    return this.current === SubscriptionType.Maths
  }

  get isCurrentWriting() {
    return this.current === SubscriptionType.Writing
  }

  get others() {
    if (!this.current) return
    return Object.values(SubscriptionType).filter((subType) => subType !== this.current)
  }

  /**
   * Note that a subscription may not necessarily have a subco.
   */
  get currentSubco(): Teacher | undefined {
    const { current } = this
    if (!current) return

    return this.store
      .peekAll<SubscriptionCoordinator>('subscription-coordinator')
      .find((subscriptionCoordinator) => subscriptionCoordinator.isForSubscriptionType(current))?.teacher
  }
}

export default SubscriptionTypeService

declare module '@ember/service' {
  interface Registry {
    'subscription-type': SubscriptionTypeService
  }
}
